<template>
  <div class="tabs-container">
    <div class="tabs" ref="tabsContainer">
      <div v-for="(option, index) in options" :key="option.value" style="display: contents;">
        <input
          type="radio"
          :id="getInputId(index)"
          :name="name"
          :value="option.value"
          v-model="model"
          :disabled="disabled"
          :key="`input-${option.value}`"
        />
        <label
          class="tab"
          :for="getInputId(index)"
          ref="tabLabel"
          :key="`label-${option.value}`"
        >
          {{ option.label }}
        </label>
      </div>
      <span class="glider" :style="gliderStyle"></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'base-slider',
  props: {
    options: {
      type: Array,
      required: true,
      validator: (value) => {
        return Array.isArray(value) && value.every(opt => opt && opt.value !== undefined && opt.label !== undefined);
      },
      description: "Array of options. E.g., [{ value: 'val1', label: 'Label 1' }, ...]"
    },
    value: {
      type: [String, Number, Boolean],
      required: true,
      description: "The currently selected value (use with v-model)."
    },
    name: {
      type: String,
      required: true,
      description: "The name attribute shared by all radio buttons in the group."
    },
    disabled: {
      type: Boolean,
      default: false,
      description: "Whether the entire slider is disabled."
    }
  },
  data() {
    return {
      gliderWidth: 0,
      gliderTranslateX: 0,
      isCalculating: false,
      observer: null // To hold our IntersectionObserver instance
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      }
    },
    gliderStyle() {
      return {
        // Use opacity to prevent a flash of the glider before its size is calculated
        opacity: this.gliderWidth > 0 ? 1 : 0,
        transform: `translateX(${this.gliderTranslateX}px)`,
        width: `${this.gliderWidth}px`,
      };
    }
  },
  methods: {
    getInputId(index) {
      return `radio-${this.name}-${this.options[index].value || index}`;
    },
    updateGliderStyle() {
      if (this.isCalculating || !this.$refs.tabLabel || this.$refs.tabLabel.length === 0) {
        return;
      }
      this.isCalculating = true;

      // Use nextTick to ensure Vue has rendered the elements before we measure
      this.$nextTick(() => {
        try {
          const currentIndex = this.options.findIndex(option => option.value === this.value);

          if (currentIndex !== -1 && this.$refs.tabLabel[currentIndex]) {
            const selectedLabelElement = this.$refs.tabLabel[currentIndex];
            const newWidth = selectedLabelElement.offsetWidth;

            // Only update if a valid width was measured
            if (newWidth > 0) {
              let newTranslateX = 0;
              for (let i = 0; i < currentIndex; i++) {
                if (this.$refs.tabLabel[i]) {
                  newTranslateX += this.$refs.tabLabel[i].offsetWidth;
                }
              }
              this.gliderWidth = newWidth;
              this.gliderTranslateX = newTranslateX;
            } else {
                console.warn("BaseSlider: Measured width is still 0. This can happen if the component is still hidden.");
            }
          }
        } catch (error) {
          console.error("BaseSlider: Error calculating glider style:", error);
        } finally {
          this.isCalculating = false;
        }
      });
    }
  },
  watch: {
    value: {
      handler() {
        // Only run subsequent updates if the glider is already visible and sized
        if (this.gliderWidth > 0) {
          this.updateGliderStyle();
        }
      }
    },
    options: {
      deep: true,
      handler() {
        // Recalculate if options change, in case the element is already visible
        if (this.gliderWidth > 0) {
          this.updateGliderStyle();
        }
      }
    }
  },
  mounted() {
    // Create an observer that will call updateGliderStyle when the component becomes visible
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // If the component is on screen and we haven't successfully calculated the width yet
        if (entry.isIntersecting && this.gliderWidth === 0) {
          console.log("BaseSlider is now visible. Calculating initial style.");
          this.updateGliderStyle();
          
          // Once the initial calculation is done, we don't need to observe anymore
          this.observer.disconnect();
        }
      },
      {
        threshold: 0.1 // Trigger when at least 10% of the element is visible
      }
    );

    // Start observing the component's root element (`<div class="tabs-container">`)
    this.observer.observe(this.$el);
  },
  beforeDestroy() {
    // Clean up the observer when the component is destroyed to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
    }
  }
};
</script>

<style scoped>
/* Base styles remain mostly the same */
.tabs-container {
  display: inline-block;
}

.tabs {
  display: flex;
  position: relative;
  background-color: #fff;
  box-shadow: 0 0 1px 0 rgba(24, 94, 224, 0.15), 0 6px 12px 0 rgba(24, 94, 224, 0.15);
  padding: 0.75rem;
  /* This padding affects offset calculation */
  border-radius: 99px;
  border-color: #f5f6fa;
  /* align-items: center; */
  /* Ensure vertical alignment if needed */
}

.tabs * {
  z-index: 2;
}

input[type="radio"] {
  display: none;
}

.tab {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 15px;
  /* Horizontal padding defines space */
  width: auto;
  white-space: nowrap;
  font-size: .8rem;
  color: black;
  font-weight: 500;
  border-radius: 99px;
  cursor: pointer;
  transition: color 0.15s ease-in;
  margin-bottom: 0;
}

input[type="radio"]:checked+label.tab {
  color: #ffffff;
}

.glider {
  position: absolute;
  /* top/left position based on parent padding */
  top: 0.75rem;
  left: 0.75rem;
  /* Glider starts aligning with the first tab inside the padding */
  height: 30px;
  /* Match tab height */
  background-color: #4a155d;
  z-index: 1;
  border-radius: 99px;
  transition: transform 0.25s ease-out, width 0.25s ease-out;
  /* Animate width too */
  /* Width is now set dynamically via :style */
  /* Remove fixed width: width: 80px; */
}

@media (max-width: 700px) {
  /* Adjust styles as needed */
}
</style>